/**
 * WhatsAppPreviewModal
 * Vista previa de mensaje WhatsApp en mockup de móvil.
 * type: "tarjeta" (tarjeta_visita_v2) | "accesos" (acceso_plataforma)
 */
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

export default function WhatsAppPreviewModal({
  open,
  onClose,
  type,
  recipientName,
  senderName,
  tarjetaUrl,
  telefono,
}) {
  const [imgError, setImgError] = useState(false);
  useEffect(() => { setImgError(false); }, [tarjetaUrl, open]);

  if (!open) return null;

  const contact = recipientName || "Contacto";
  const sender = senderName || "ConextCRM";
  const now = new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

  const telefonoComercial = telefono || "+34 600 00 00 00";
  const tarjetaBody =
    `Hola ${contact}, te comparto mi tarjeta de contacto tras la visita de hoy.\n\n` +
    `Para cualquier duda sobre el sistema que vimos, escríbeme o llámame directamente a mi WhatsApp: ${telefonoComercial}\n\n` +
    `Ese es mi número directo (lo tienes también en el QR y en la tarjeta de arriba). Así contactas conmigo al instante.\n\n` +
    `Gracias por tu tiempo. Un saludo de parte de ${sender}, quedo a tu disposición.`;

  // Cuerpo REAL de la plantilla `acceso_plataforma` aprobada en Meta, con los
  // parámetros que le pasan los dos callers (SectorPickerModal y LeadEditModal):
  //   {{1}} = "<usuario>  Contraseña: <password>"   {{2}} = "app.conextcrm.es"
  // Los emojis son los del cuerpo aprobado: la preview lo reproduce tal cual.
  const accesosBody =
    `¡Tu cuenta en ConextCRM está lista! 🎉\n\n` +
    `  👤 Usuario: 600111222  Contraseña: 600111222\n\n` +
    `accede aquí:\n  app.conextcrm.es\n\n` +
    `¡Cualquier duda, aquí estamos!`;

  const body = type === "accesos" ? accesosBody : tarjetaBody;
  const title = type === "accesos" ? "Vista previa · Accesos" : "Vista previa · Tarjeta de visita";

  return (
    <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Content — scroll vertical si la pantalla es pequeña */}
      <div className="relative flex flex-col items-center gap-4 overflow-y-auto max-h-[95vh] px-2 py-2">
        {/* Title + close */}
        <div className="flex items-center gap-3">
          <p className="text-white font-bold text-sm tracking-wide">{title}</p>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition text-white"
          >
            <X size={14} />
          </button>
        </div>

        {/* Phone frame — escala con la altura disponible */}
        <div
          className="relative rounded-[44px] shadow-2xl"
          style={{
            width: "min(400px, calc((100vh - 160px) * 0.565))",
            background: "#18181b",
            padding: 9,
            border: "2px solid #3f3f46",
          }}
        >
          {/* Notch pill */}
          <div
            className="absolute left-1/2 -translate-x-1/2 bg-[#18181b] rounded-full z-10"
            style={{ top: 12, width: 72, height: 18 }}
          />

          {/* Screen */}
          <div
            className="rounded-[36px] overflow-hidden flex flex-col"
            style={{ height: "min(710px, calc(100vh - 160px))", background: "#ECE5DD" }}
          >
            {/* WhatsApp header */}
            <div className="bg-[#075E54] px-3 py-2 pt-7 flex items-center gap-2.5 flex-shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-[11px] font-bold text-gray-600 flex-shrink-0">
                {contact.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-[12px] font-semibold leading-tight truncate">{contact}</p>
                <p className="text-[#b2dfdb] text-[10px]">en línea</p>
              </div>
              {/* three-dot icon */}
              <div className="flex flex-col gap-[3px] px-1 opacity-80">
                {[0,1,2].map(i => <div key={i} className="w-1 h-1 rounded-full bg-white" />)}
              </div>
            </div>

            {/* Chat wallpaper area */}
            <div
              className="flex-1 overflow-y-auto p-3 flex flex-col gap-2"
              style={{ background: "url('data:image/svg+xml,%3Csvg width%3D%2260%22 height%3D%2260%22 viewBox%3D%220 0 60 60%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E%3Cg fill%3D%22%23c9b8a0%22 fill-opacity%3D%220.18%22%3E%3Cpath d%3D%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2FSvg%3E') #ECE5DD" }}
            >
              {/* Outgoing bubble */}
              <div className="self-end max-w-[88%]">
                <div
                  className="shadow-sm overflow-hidden"
                  style={{ background: "#DCF8C6", borderRadius: "12px 12px 3px 12px" }}
                >
                  {/* Header image — tarjeta only */}
                  {type === "tarjeta" && (
                    tarjetaUrl && !imgError ? (
                      <img
                        src={tarjetaUrl}
                        alt="Tarjeta de visita"
                        className="w-full block"
                        style={{ maxHeight: 220, objectFit: "cover" }}
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div
                        className="w-full flex flex-col items-center justify-center gap-1 text-slate-400"
                        style={{ height: 100, background: "#f1f5f9" }}
                      >
                        <span style={{ fontSize: 28 }}>🪪</span>
                        <span style={{ fontSize: 10, fontWeight: 600 }}>Tu tarjeta de visita</span>
                      </div>
                    )
                  )}

                  {/* Message text */}
                  <div className="px-3 py-2">
                    <p
                      className="leading-relaxed whitespace-pre-line"
                      style={{ fontSize: 12, color: "#111", lineHeight: "1.55" }}
                    >
                      {body}
                    </p>
                    {/* Timestamp + double tick read */}
                    <div className="flex justify-end items-center gap-0.5 mt-1">
                      <span style={{ fontSize: 10, color: "#8a9b8a" }}>{now}</span>
                      <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                        <path d="M1 5L4 8L9.5 1" stroke="#53BDEB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.5 5L8.5 8L14 1" stroke="#53BDEB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Input bar */}
            <div className="bg-[#F0F0F0] px-2.5 py-2 flex items-center gap-2 flex-shrink-0">
              <div className="w-7 h-7 flex items-center justify-center text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </div>
              <div className="flex-1 bg-white rounded-full px-3.5 py-1.5 text-gray-400" style={{ fontSize: 11 }}>
                Escribe un mensaje
              </div>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "#075E54" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Accesos disclaimer */}
        {type === "accesos" && (
          <p className="text-white/50 text-[11px] text-center max-w-[280px]">
            El usuario y el enlace de acceso reales se generan al enviar
          </p>
        )}
      </div>
    </div>
  );
}
